import { useLiveQuery } from 'dexie-react-hooks';
import { EXERCISES, findExercise } from '@/data/exercises';
import type { BodyWeight, Exercise, RoutineTemplate, Workout, WorkoutSet } from '@/engine/types';
import {
  db,
  newId,
  nowIso,
  putAndQueue,
  softDeleteAndQueue,
  type ExerciseRow,
  type RoutineRow,
  type WorkoutRow,
} from '@/lib/db';

export function useWorkout(date: string): WorkoutRow | null | undefined {
  return useLiveQuery(async () => {
    const rows = await db.workouts.where('date').equals(date).toArray();
    return rows.find((r) => r.deletedAt === null) ?? null;
  }, [date]);
}

/**
 * Catálogo completo: los del repositorio más los del usuario.
 *
 * Lo usan también el motor de avisos y Progreso: un ejercicio propio que no
 * entra en el catálogo de esos dos existe solo a medias —se registra, pero ni
 * cuenta para el volumen por músculo ni progresa— y eso es peor que no poder
 * crearlo.
 */
export function mergeExercises(custom: readonly ExerciseRow[]): Exercise[] {
  return [...EXERCISES, ...custom.filter((e) => e.deletedAt === null)];
}

/** Ejercicios del catálogo más los que haya creado el usuario. */
export function useExercises(): Exercise[] {
  const custom = useLiveQuery(async () => db.exercises.toArray(), [], []);
  return mergeExercises(custom ?? []);
}

/**
 * Crea un ejercicio propio. Se sincroniza como todo lo demás, así que aparece
 * en los otros dispositivos.
 */
export async function createCustomExercise(
  exercise: Omit<Exercise, 'id' | 'custom'>,
): Promise<string> {
  const id = `custom-${newId()}`;
  await putAndQueue('exercises', id, {
    ...exercise,
    id,
    custom: true,
    updatedAt: nowIso(),
    deletedAt: null,
  });
  return id;
}

export function exerciseById(id: string, all: readonly Exercise[]): Exercise | undefined {
  return findExercise(id) ?? all.find((e) => e.id === id);
}

async function saveWorkout(workout: Workout): Promise<void> {
  await putAndQueue('workouts', workout.id, {
    ...workout,
    updatedAt: nowIso(),
    deletedAt: null,
  });
}

/** Crea la sesión del día si no existe todavía. */
export async function ensureWorkout(date: string): Promise<WorkoutRow> {
  const rows = await db.workouts.where('date').equals(date).toArray();
  const existing = rows.find((r) => r.deletedAt === null);
  if (existing) return existing;

  const workout: Workout = { id: newId(), date, exerciseOrder: [], sets: [] };
  const row: WorkoutRow = { ...workout, updatedAt: nowIso(), deletedAt: null };
  await putAndQueue('workouts', row.id, row);
  return row;
}

/**
 * Leer la sesión, cambiarla y guardarla en UNA transacción. Dexie encadena las
 * transacciones de escritura sobre las mismas tablas, así que dos toques
 * seguidos se ejecutan uno detrás de otro en vez de pisarse (antes, un doble
 * toque perdía una serie o creaba dos sesiones para el mismo día).
 */
async function updateWorkout(
  date: string,
  change: (w: WorkoutRow) => Workout | null,
): Promise<void> {
  await db.transaction('rw', db.workouts, db.outbox, async () => {
    const next = change(await ensureWorkout(date));
    if (next) await saveWorkout(next);
  });
}

export async function addExerciseToWorkout(date: string, exerciseId: string): Promise<void> {
  await updateWorkout(date, (workout) =>
    workout.exerciseOrder.includes(exerciseId)
      ? null
      : { ...workout, exerciseOrder: [...workout.exerciseOrder, exerciseId] },
  );
}

export async function removeExerciseFromWorkout(date: string, exerciseId: string): Promise<void> {
  await updateWorkout(date, (workout) => ({
    ...workout,
    exerciseOrder: workout.exerciseOrder.filter((id) => id !== exerciseId),
    sets: workout.sets.filter((s) => s.exerciseId !== exerciseId),
  }));
}

export async function addSet(date: string, set: Omit<WorkoutSet, 'id' | 'order'>): Promise<void> {
  await updateWorkout(date, (workout) => ({
    ...workout,
    exerciseOrder: workout.exerciseOrder.includes(set.exerciseId)
      ? workout.exerciseOrder
      : [...workout.exerciseOrder, set.exerciseId],
    sets: [
      ...workout.sets,
      {
        ...set,
        id: newId(),
        order: workout.sets.filter((s) => s.exerciseId === set.exerciseId).length,
      },
    ],
  }));
}

export async function removeSet(date: string, setId: string): Promise<void> {
  await updateWorkout(date, (workout) => ({
    ...workout,
    sets: workout.sets.filter((s) => s.id !== setId),
  }));
}

export async function deleteWorkout(id: string): Promise<void> {
  await softDeleteAndQueue('workouts', id);
}

/**
 * Última serie efectiva de un ejercicio, para precargar la siguiente.
 *
 * Es lo que evita teclear entre series: casi siempre se repite el peso del
 * último día, y cuando no, se ajusta con dos toques.
 */
export function useLastSet(exerciseId: string, beforeDate: string): WorkoutSet | null {
  const found = useLiveQuery(async () => {
    const rows = await db.workouts.orderBy('date').reverse().limit(60).toArray();
    for (const workout of rows) {
      if (workout.deletedAt !== null || workout.date >= beforeDate) continue;
      const sets = workout.sets.filter((s) => s.exerciseId === exerciseId && !s.warmup);
      if (sets.length > 0) return sets[sets.length - 1] ?? null;
    }
    return null;
  }, [exerciseId, beforeDate]);
  return found ?? null;
}

// --- Peso corporal ---

export function useBodyWeights(): BodyWeight[] {
  const rows = useLiveQuery(async () => db.bodyWeights.toArray(), [], []);
  return (rows ?? [])
    .filter((r) => r.deletedAt === null)
    .map(({ date, kg }) => ({ date, kg }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export async function setBodyWeight(date: string, kg: number): Promise<void> {
  await putAndQueue('bodyWeights', date, { date, kg, updatedAt: nowIso(), deletedAt: null });
}

export function useTodayWeight(date: string): number | null {
  const row = useLiveQuery(async () => (await db.bodyWeights.get(date)) ?? null, [date]);
  return row && row.deletedAt === null ? row.kg : null;
}

// --- Plantillas de rutina ---

export function useRoutines(): RoutineRow[] {
  const rows = useLiveQuery(async () => db.routines.toArray(), [], []);
  return (rows ?? []).filter((r) => r.deletedAt === null);
}

/** Guarda el orden de ejercicios de la sesion actual como plantilla. */
export async function saveRoutine(name: string, exerciseIds: string[]): Promise<void> {
  const routine: RoutineTemplate = { id: newId(), name: name.trim(), exerciseIds };
  await putAndQueue('routines', routine.id, {
    ...routine,
    updatedAt: nowIso(),
    deletedAt: null,
  });
}

export async function deleteRoutine(id: string): Promise<void> {
  await softDeleteAndQueue('routines', id);
}

/** Carga los ejercicios de una plantilla en la sesion del dia. */
export async function applyRoutine(date: string, routine: RoutineTemplate): Promise<void> {
  const workout = await ensureWorkout(date);
  const merged = [...workout.exerciseOrder];
  for (const id of routine.exerciseIds) {
    if (!merged.includes(id)) merged.push(id);
  }
  await putAndQueue('workouts', workout.id, {
    ...workout,
    exerciseOrder: merged,
    updatedAt: nowIso(),
  });
}
